import { ReactNode, useEffect, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { Icon } from '@/new/components/icon';
import { cn } from '@/new/lib/utils';

interface CollapsibleReasoningProps {
  isComplete: boolean;
  children: ReactNode;
}

export function CollapsibleReasoning({ isComplete, children }: CollapsibleReasoningProps) {
  const [isOpen, setIsOpen] = useState(!isComplete);

  useEffect(() => {
    if (isComplete) {
      setIsOpen(false);
    }
  }, [isComplete]);

  return (
    // Using custom sort of accordion here because the radix one is actually not super nice, if you
    // switch the direction half way through it resets the animation. This is nicer.
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="mx-auto max-w-3xl px-4"
    >
      <button
        onClick={() => setIsOpen((o) => !o)}
        className="my-2 flex items-center gap-2 text-xs font-normal text-gray-400 duration-150 hover:text-gray-600"
      >
        <Icon
          className={cn('lucide--chevron-down text-gray-400 transition-transform duration-200', isOpen && 'rotate-180')}
        />
        Thinking and analysis
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="ml-1.25">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
